import { useId } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { chatMarkdownComponents } from "@/lib/markdownHelpers";
import AgentChatRenderer, { specNeedsContentFallback } from "./agentChat/AgentChatRenderer";
import { extractToolResults, hasLegacyM360Fences, parseAgentMessage } from "./agentChat/parseAgentMessage";
import { mergeProfilePhotosIntoBlock, mergeProfilePhotosIntoSpec } from "./agentChat/profilePhotoFromToolResults";
import {
  hasRenderableStructuredBlock,
  isPeopleStructuredBlock,
  mergeDumpFieldsIntoSkinnyPeopleSpec,
  mergeSpecLinksIntoPromotedBlock,
  shouldPreferEventCardsOverSpec,
  shouldPreferPeopleDumpOverSpec,
  shouldPreferToolResultEventsOverSpec,
  specLooksLikePeopleList
} from "./agentChat/specOverrides";
import M360StructuredBlockView from "./M360StructuredBlock";
import {
  collapseRedundantAssistantText,
  parseChatContentSegments,
  promoteAgentProseToStructuredBlock,
  sanitizeToolResultsMarkdown
} from "./m360StructuredContent";

type ChatRichContentProps = {
  text: string;
};

/** When a PersonCard carries M360 facts, prose should only add a From the web section. */
function proseForPersonCard(prose: string): string | null {
  const trimmed = prose.trim();
  if (!trimmed) {
    return null;
  }

  const headingMatch = /(?:^|\n)((?:#{1,3}\s*)?(?:\*\*)?From the web(?:\*\*)?[^\n]*)/i.exec(trimmed);
  if (!headingMatch || headingMatch.index === undefined) {
    return null;
  }

  const headingStart = headingMatch.index + (headingMatch[0].startsWith("\n") ? 1 : 0);
  const fromHeading = trimmed.slice(headingStart);
  const followingPeopleDump = /\nFound \d+ matching (?:person|people)(?: \(showing \d+\))?:?/i.exec(fromHeading);
  const section = (followingPeopleDump ? fromHeading.slice(0, followingPeopleDump.index) : fromHeading).trim();

  return section.length > 0 ? section : null;
}

function FromTheWebPanel({ markdown }: { markdown: string }) {
  const headingId = useId();
  const trimmed = markdown.trim();
  const headingMatch = /^(?:#{1,3}\s*)?(?:\*\*)?From the web(?:\*\*)?[^\n]*\n?([\s\S]*)/i.exec(trimmed);
  const body = (headingMatch?.[1] ?? trimmed).trim();

  return (
    <section
      className="rounded-xl border border-dashed border-sys-brand-outline-variant bg-sys-brand-surface-container-low/60 p-3"
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className="mb-2 text-label-large text-sys-brand-on-surface-variant">
        From the web
      </h3>
      {body ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
          {body}
        </ReactMarkdown>
      ) : null}
    </section>
  );
}

function JsonPanel({ value }: { value: unknown }) {
  const formatted = JSON.stringify(value, null, 2);

  return (
    <pre className="overflow-x-auto rounded-xl border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-3 text-body-small text-sys-brand-on-surface">
      <code>{formatted}</code>
    </pre>
  );
}

function LegacyStructuredContent({ text }: { text: string }) {
  const segments = parseChatContentSegments(text);

  return (
    <div className="space-y-3">
      {segments.map((segment, index) => {
        switch (segment.kind) {
          case "structured":
            return <M360StructuredBlockView key={`structured-${index}`} block={segment.value} />;
          case "json":
            return <JsonPanel key={`json-${index}`} value={segment.value} />;
          case "markdown":
            return (
              <ReactMarkdown key={`md-${index}`} remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
                {segment.value}
              </ReactMarkdown>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export default function ChatRichContent({ text }: ChatRichContentProps) {
  const { text: messageBody, toolResults } = extractToolResults(text);
  const { prose, spec } = parseAgentMessage(messageBody);
  const useLegacy = !spec && hasLegacyM360Fences(messageBody);

  if (useLegacy) {
    const legacySource = [messageBody, toolResults].filter(Boolean).join("\n\n");
    return <LegacyStructuredContent text={legacySource} />;
  }

  const fallbackSource = [prose, toolResults].filter(Boolean).join("\n\n");
  const promotedBlock = fallbackSource ? promoteAgentProseToStructuredBlock(fallbackSource) : null;
  const renderablePromoted = hasRenderableStructuredBlock(promotedBlock) ? promotedBlock : null;
  const mergedPromotedBlock = mergeProfilePhotosIntoBlock(
    mergeSpecLinksIntoPromotedBlock(spec, renderablePromoted),
    toolResults
  );
  const mergedSpec = mergeDumpFieldsIntoSkinnyPeopleSpec(
    mergeProfilePhotosIntoSpec(spec, toolResults),
    mergedPromotedBlock
  );
  const replaceSpecWithEvents = Boolean(
    mergedPromotedBlock && mergedSpec && shouldPreferEventCardsOverSpec(mergedSpec, mergedPromotedBlock)
  );
  const replaceSpecWithToolResultEvents = Boolean(
    mergedPromotedBlock && mergedSpec && shouldPreferToolResultEventsOverSpec(mergedSpec, mergedPromotedBlock)
  );
  const replaceSpecWithPeopleDump = Boolean(
    mergedPromotedBlock && mergedSpec && shouldPreferPeopleDumpOverSpec(mergedSpec, mergedPromotedBlock)
  );
  const specIsBroken = Boolean(mergedSpec && specNeedsContentFallback(mergedSpec));
  const shouldFallbackToPromoted = Boolean(
    mergedPromotedBlock &&
    (!mergedSpec ||
      specIsBroken ||
      replaceSpecWithEvents ||
      replaceSpecWithToolResultEvents ||
      replaceSpecWithPeopleDump)
  );
  const proseFallbackBlock = shouldFallbackToPromoted ? mergedPromotedBlock : null;
  const peopleFallback = Boolean(proseFallbackBlock && isPeopleStructuredBlock(proseFallbackBlock));
  const showProse = Boolean(prose) && !proseFallbackBlock;
  const showSpec = Boolean(mergedSpec) && !proseFallbackBlock && !specIsBroken;
  const isPeopleSpec = showSpec && specLooksLikePeopleList(mergedSpec);
  const showPeopleWebPanel = isPeopleSpec || peopleFallback;
  const collapsedProse = collapseRedundantAssistantText(prose);
  const displayProse = showPeopleWebPanel ? proseForPersonCard(collapsedProse) : showProse ? collapsedProse : null;
  const sanitizedToolResults = toolResults ? sanitizeToolResultsMarkdown(toolResults) : null;
  const showToolResultsMarkdown = Boolean(sanitizedToolResults && !proseFallbackBlock && !showSpec);
  const peopleSpecFirst = isPeopleSpec;

  const hasVisibleContent = Boolean(displayProse || proseFallbackBlock || showSpec || showToolResultsMarkdown);

  return (
    <div className="space-y-3">
      {peopleSpecFirst ? <AgentChatRenderer spec={mergedSpec} /> : null}
      {peopleFallback && proseFallbackBlock ? <M360StructuredBlockView block={proseFallbackBlock} /> : null}
      {displayProse ? (
        showPeopleWebPanel ? (
          <FromTheWebPanel markdown={displayProse} />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
            {displayProse}
          </ReactMarkdown>
        )
      ) : null}
      {proseFallbackBlock && !peopleFallback ? <M360StructuredBlockView block={proseFallbackBlock} /> : null}
      {showSpec && !peopleSpecFirst ? <AgentChatRenderer spec={mergedSpec} /> : null}
      {showToolResultsMarkdown ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatMarkdownComponents}>
          {sanitizedToolResults}
        </ReactMarkdown>
      ) : null}
      {hasVisibleContent ? null : (
        <p className="text-body-medium text-sys-brand-on-surface-variant">No answer was returned. Please try again.</p>
      )}
    </div>
  );
}
